import type { BuildConfig } from './build.types';
import type { CheckoutInstance, EkkoCheckoutGlobal } from './core.types';
import { createAppLoader } from './app-loader';
import { preconnect } from './hints';
import { createInstance, type Instance } from './instance';
import { resolveOptions, warn } from './options';

export const LOADED_EVENT = 'ekko:checkout-loaded';

function currentNonce(): string {
  const script = document.currentScript as HTMLScriptElement | null;
  return (script && (script.nonce || script.getAttribute('nonce'))) || '';
}

/**
 * Runs once, when core.js is parsed. The first core on a page owns
 * `window.EkkoCheckout`; any later copy warns and does nothing else, so it does
 * not fetch a second app either.
 */
export function boot(config: BuildConfig): void {
  if (window.EkkoCheckout) return warn('DUPLICATE_CORE', 'EkkoCheckout is already on the page.');

  const loader = createAppLoader(config, currentNonce());
  const live = new WeakMap<HTMLElement, Instance>();

  function initCheckout(options: unknown): CheckoutInstance {
    const resolved = resolveOptions(options, config.modes);
    const previous = live.get(resolved.container);
    if (previous) {
      const state = previous.state();
      if (state !== 'destroyed' && state !== 'failed') {
        warn('DUPLICATE_INIT', 'the checkout already in this container is destroyed.');
      }
      previous.api.destroy();
    }
    const instance = createInstance(resolved, config, loader.load(), loader.loadScript);
    live.set(resolved.container, instance);
    return instance.api;
  }

  const global: EkkoCheckoutGlobal = { initCheckout, version: config.version };
  window.EkkoCheckout = global;
  preconnect(config);
  if (config.appLoad === 'parse') loader.load();
  window.dispatchEvent(new Event(LOADED_EVENT));
}
